import { ViewPort } from "./View/ViewPort";
import { IShader } from "./Shader";
import { Scene } from "./Scene";
import { TickEventManager } from "@spi/Common/Util/TickEventManager";
import { ShaderManager } from "./Shader/";
import { Camera } from "./View/Camera";
import { ContextBuilder } from "./Context/ContextBuilder";
import { ResourceLoader } from "@spi/Common/Resource/ResourceLoader";
import { SceneRenderer, DefaultSceneRenderer } from "./Renderer";
import { RenderedTexture } from "./Texture";
import { AdditiveBlendOverlay, TextureOverlay } from "./Object/Overlay";
import { OverlaySceneRenderer } from "./Renderer/OverlaySceneRenderer";

export class Engine {

    private isActive: boolean;
    private activeRenderer: SceneRenderer;
    private activeFrameBuffer: WebGLFramebuffer;

    private _tickEventManager: TickEventManager;
    private _contextBuilder: ContextBuilder;
    private _ctx: Context;
    private _activeScene: Scene;
    private _shaderManager: ShaderManager;

    constructor(contextBuilder: ContextBuilder, public activeCamera: Camera) {
        this._contextBuilder = contextBuilder;
        this._ctx = contextBuilder.createContext(this);

        this._tickEventManager = new TickEventManager();
        this._activeScene = null;
        this._shaderManager = new ShaderManager(this.ctx.gl);
    }

    public get ctx(): Context {
        return this._ctx;
    }

    public get activeScene(): Scene {
        return this._activeScene;
    }

    public get shaderManager(): ShaderManager {
        return this._shaderManager;
    }

    public get contextBuilder(): ContextBuilder {
        return this._contextBuilder;
    }

    public get tickEventManager(): TickEventManager {
        return this._tickEventManager;
    }

    public overlayRenderer: OverlaySceneRenderer;

    public activateScene(scene: Scene) {
        if (!scene) throw new Error('scene must be set');

        if (this.activeScene) {
            this.activeRenderer.destroy();
            this.activeScene.detached();

            this.activeRenderer = null;
            this._activeScene = null;
        }

        this.overlayRenderer = new OverlaySceneRenderer();
        this.overlayRenderer.init(this.ctx);

        this.activeFrameBuffer = null; // Note: rendering to null means rendering to canvas
        this._activeScene = scene;
        this.activeRenderer = scene.createRenderer();
        this.activeRenderer.init(this.ctx);
        scene.attached();
    }

    public stop() {
        this.isActive = false;
    }

    public start() {
        this.isActive = true;
        this.renderLoop();
    }

    private renderLoop() {
        if (!this.isActive) {
            return;
        }

        this.tickEventManager.tick();
        this.activeScene.tickEventManager.tick();

        this.renderFrame(this.activeRenderer, this.activeCamera);

        requestAnimationFrame(() => this.renderLoop());
    }



    public renderFrame(renderer: SceneRenderer, camera: Camera) {

        if (!this.activateScene) {
            throw new Error(`No active scene has been set`);
        }

        camera.prepareFrame();

        renderer.render(this.activeScene, camera, this.activeFrameBuffer);

        if(this.overlayRenderer.overlay) {
            this.overlayRenderer.render(this.activeScene, camera, this.activeFrameBuffer);
        }

    }
}

export class Context {
    private currentShader: IShader;

    constructor(public engine: Engine, public gl: WebGLRenderingContext, public viewPort: ViewPort, public resourceLoader: ResourceLoader) {
        this.currentShader = null;
    }

    public setShader(shader: IShader) {
        if (this.currentShader != shader) {
            if (this.currentShader) {
                this.currentShader.disableAttributes();
            }

            this.currentShader = shader;
            this.gl.useProgram(this.currentShader.program);

            this.currentShader.enableAttributes();
        }
        return this.currentShader;
    }
}


export type EngineSettings = {
    viewport: {
        width: number,
        height: number
    },
    keymap: {
        [key: string]: [
            number[]
        ]
    }
}

